import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { ExclamationTriangleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface LicenseAlertCompany {
  company_id: string;
  company_name: string;
  reason: string;
  detail: string;
  available_licenses?: number;
  total_licenses?: number;
  licensed_until?: string;
  days_left?: number;
  last_notification_at: number;
}

// Snake to camel for API payload fallback (e.g. available_licenses -> availableLicenses)
function toCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}
function payloadNum(p: Record<string, unknown>, key: string): number | undefined {
  const v = p[key] ?? p[toCamel(key)];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}
function payloadStr(p: Record<string, unknown>, key: string): string | undefined {
  const v = p[key] ?? p[toCamel(key)];
  return typeof v === 'string' ? v : undefined;
}

const AdministrationLicenses: React.FC = () => {
  const { t } = useTranslation('licenses');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightCompanyId = searchParams.get('company_id');
  const { user } = useAuth();
  usePageTitle(t('title'));

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications-license-alerts'],
    queryFn: async () => {
      const res = await apiService.getNotifications(false);
      return res.notifications || [];
    },
  });

  const companiesFromNotifications = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const byCompany = new Map<string, LicenseAlertCompany>();
    for (const n of data) {
      const type = n.type || (n.payload as Record<string, unknown>)?.type;
      if (type !== 'license_alert') continue;
      const p = (n.payload || {}) as Record<string, unknown>;
      const companyId = (p.company_id ?? p.companyId) as string | undefined;
      if (!companyId) continue;
      const createdAt = typeof n.created_at === 'number' ? n.created_at : (n.created_at ? new Date(n.created_at as string | number).getTime() / 1000 : 0);
      const existing = byCompany.get(companyId);
      if (!existing || createdAt > existing.last_notification_at) {
        const avail = payloadNum(p, 'available_licenses');
        const total = payloadNum(p, 'total_licenses');
        byCompany.set(companyId, {
          company_id: companyId,
          company_name: (p.company_name ?? p.companyName ?? 'Unknown') as string,
          reason: (p.reason ?? '') as string,
          detail: (payloadStr(p, 'detail') ?? '') as string,
          available_licenses: avail,
          total_licenses: total,
          licensed_until: payloadStr(p, 'licensed_until'),
          days_left: payloadNum(p, 'days_left'),
          last_notification_at: createdAt,
        });
      }
    }
    return Array.from(byCompany.values()).sort((a, b) => a.company_name.localeCompare(b.company_name));
  }, [data]);

  // For rows missing license details, fetch company to fill available_licenses / total_licenses
  const companyIdsToEnrich = useMemo(
    () => companiesFromNotifications.filter((c) => c.available_licenses === undefined && c.total_licenses === undefined).map((c) => c.company_id),
    [companiesFromNotifications]
  );

  const companyEnrichQueries = useQueries({
    queries: companyIdsToEnrich.map((companyId) => ({
      queryKey: ['company-license-detail', companyId],
      queryFn: async () => {
        const res = await apiService.get(`/companies/${companyId}`);
        return res.data as { available_licenses?: number; total_licenses?: number; settings?: string };
      },
      enabled: companyIdsToEnrich.length > 0,
    })),
  });

  const companyDetailById = useMemo(() => {
    const map: Record<string, { available_licenses?: number; total_licenses?: number; detail?: string }> = {};
    companyEnrichQueries.forEach((q, i) => {
      if (q.data && companyIdsToEnrich[i]) {
        const d = q.data;
        let detail = '';
        if (d.available_licenses !== undefined && d.total_licenses !== undefined) {
          detail = `${d.available_licenses} of ${d.total_licenses} licenses left`;
        } else if (d.available_licenses !== undefined) {
          detail = `${d.available_licenses} licenses left`;
        }
        if (d.settings) {
          try {
            const settings = JSON.parse(d.settings);
            const until = settings?.licensed_until;
            if (until) {
              const exp = new Date(until);
              if (!Number.isNaN(exp.getTime()) && exp.getTime() > Date.now()) {
                const days = Math.floor((exp.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
                detail = detail ? `${detail}; ${days} days left` : `${days} days left`;
              }
            }
          } catch {
            // ignore
          }
        }
        map[companyIdsToEnrich[i]] = {
          available_licenses: d.available_licenses,
          total_licenses: d.total_licenses,
          detail: detail || undefined,
        };
      }
    });
    return map;
  }, [companyEnrichQueries, companyIdsToEnrich]);

  const companies = useMemo(() => {
    return companiesFromNotifications.map((c) => {
      const enriched = companyDetailById[c.company_id];
      if (!enriched) return c;
      return {
        ...c,
        available_licenses: c.available_licenses ?? enriched.available_licenses,
        total_licenses: c.total_licenses ?? enriched.total_licenses,
        detail: c.detail || enriched.detail || c.detail,
      };
    });
  }, [companiesFromNotifications, companyDetailById]);

  const goToSubscription = (companyId: string) => {
    localStorage.setItem(`companyForm_activeTab_${companyId}`, 'subscription');
    navigate(`/clients/${companyId}`);
  };

  if (!user) return null;

  const isAdmin = user.role === 'admin' || user.role === 'sysadmin';
  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ExclamationTriangleIcon className="w-7 h-7 text-amber-500" />
          {t('title')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('description')}
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-red-700 dark:text-red-300">
          {t('loadError')}
        </div>
      )}

      {!isLoading && !error && companies.length === 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">{t('noAlertsYet')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
            {t('noAlertsHint')}
          </p>
        </div>
      )}

      {!isLoading && !error && companies.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('company')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('alert')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('action')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {companies.map((row) => (
                  <tr
                    key={row.company_id}
                    className={
                      highlightCompanyId === row.company_id
                        ? 'bg-amber-50 dark:bg-amber-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {row.company_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-medium">
                        {row.reason === 'expiry' ? t('reasonExpiry') : t('reasonLow')}
                      </span>
                      {(() => {
                        const detail =
                          row.detail ??
                          (row.available_licenses !== undefined && row.total_licenses !== undefined
                            ? t('licensesLeft', { available: row.available_licenses, total: row.total_licenses })
                            : row.available_licenses !== undefined
                              ? t('licensesLeftShort', { count: row.available_licenses })
                              : '');
                        return detail ? (
                          <span className="block text-gray-500 dark:text-gray-400 mt-0.5">{detail}</span>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => goToSubscription(row.company_id)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        {t('viewSubscription')}
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdministrationLicenses;
